import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import React, { ChangeEvent, FormEvent, KeyboardEvent, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { toast, Input, Button } from "@mindstonehq/ui";

interface ForgotPasswordProps {
  email: string;
  resendPIN: (e: FormEvent, email: string) => Promise<boolean>;
  submitReset: (e: FormEvent, email: string, pin: string, password: string) => Promise<boolean>;
  validatePin: (e: FormEvent, email: string, pin: string) => Promise<boolean>;
  goBack: () => void;
  setLoading: (loading: boolean) => void;
}
export default function ForgotPassword({
  email,
  resendPIN,
  submitReset,
  validatePin,
  goBack,
  setLoading,
}: ForgotPasswordProps) {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const [uiState, setUiState] = useState<"pin" | "password" | "login">("pin");
  const numInputs = 8;
  const inputArray = Array.from({ length: numInputs }, (_, index) => index);
  const inputRefs = inputArray.map(() => useRef<HTMLInputElement>(null));
  const [inputValues, setInputValues] = useState(Array(numInputs).fill(""));
  const [codeIncorrect, setCodeIncorrect] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedValues = [...inputValues];
    updatedValues[index] = event.target.value;
    setInputValues(updatedValues);

    if (event.target.value && index < numInputs - 1) {
      const ref = inputRefs[index + 1].current;
      if (ref) ref.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace" && !inputValues[index] && index > 0) {
      const ref = inputRefs[index - 1].current;
      if (ref) ref.focus();
      const updatedValues = [...inputValues];
      updatedValues[index - 1] = "";
      setInputValues(updatedValues);
    }
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    setCodeIncorrect(false);
    setLoading(true);
    try {
      await validatePin(e, email, inputValues.join(""));
      setUiState("password");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCodeIncorrect(true);
      setInputValues(Array(numInputs).fill(""));
    }
  };

  const handleResend = async (e: FormEvent) => {
    setLoading(true);
    try {
      await resendPIN(e, email);
      toast.success(t`Code sent again`);
      setLoading(false);
    } catch (error) {
      toast.error(t`Error resending code`, {
        description: t`Please refresh and try again`,
      });
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: FormEvent) => {
    setLoading(true);
    try {
      await submitReset(e, email, inputValues.join(""), newPassword);
      setUiState("login");
      setLoading(false);
    } catch (e) {
      toast.error(t`Error resetting password`, {
        description: t`Please refresh and try again`,
      });
      setLoading(false);
    }
  };

  return (
    <>
      {/* Back Button for password state - fixed positioning relative to right panel (50vw + 2rem) */}
      {uiState === "password" && (
        <button
          type="button"
          className="fixed top-6 left-[calc(50vw+2rem)] lg:left-[calc(50vw+2rem)] flex items-center text-placeholder-foreground hover:text-foreground transition-colors z-[60]"
          onClick={() => setUiState("pin")}
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          <span className="text-sm"><Trans>Back</Trans></span>
        </button>
      )}

      <div className="flex flex-col justify-center items-center w-full">
        {uiState === "pin" && (
          <>
            {/* Header */}
            <div className="text-center flex flex-col gap-2 mb-10">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Reset password</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>Enter the verification code we sent to your email</Trans>
              </p>
            </div>

            {/* Code Input Section */}
            <div className="flex flex-col gap-6 w-full">
              <div
                className="flex items-center justify-center gap-2"
                onPaste={(event) => {
                  event.preventDefault();
                  let paste = event.clipboardData.getData("text");
                  setInputValues(Array.from({ length: numInputs }, (_, index) => paste[index] ?? ""));
                }}
              >
                {inputArray.map((_, i) => {
                  return (
                    <input
                      key={i}
                      autoFocus={i === 0}
                      ref={inputRefs[i]}
                      onChange={(e) => handleInputChange(e, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      type="text"
                      aria-label={t`Code character ${i + 1}`}
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      value={inputValues[i]}
                      className="bg-background text-center p-0 h-[42px] w-[38px] border border-border hover:border-foreground focus:border-foreground focus:ring-1 focus:ring-foreground rounded-lg text-base font-medium text-foreground outline-hidden transition-colors"
                      maxLength={1}
                    />
                  );
                })}
              </div>

              <div className="text-sm text-muted-foreground text-center">
                <Trans>
                  Didn't receive the code?{" "}
                  <button className="text-primary-hover hover:underline" onClick={handleResend}>
                    Resend
                  </button>
                </Trans>
              </div>

              {codeIncorrect && (
                <div className="text-sm text-destructive text-center"><Trans>Incorrect code! Please try again.</Trans></div>
              )}

              <Button
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                onClick={handleSubmit}
                disabled={inputValues.some((value) => value === "")}
              >
                <Trans>Next</Trans>
              </Button>
            </div>
          </>
        )}

        {uiState === "password" && (
          <>
            {/* Header */}
            <div className="text-center flex flex-col gap-2 mb-10">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Create new password</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground"><Trans>Choose a password with at least 8 characters.</Trans></p>
            </div>

            {/* Form */}
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-1.5 w-full">
                <label htmlFor="password" className="text-sm font-semibold leading-5 text-foreground">
                  <Trans>Password</Trans>
                </label>
                <Input
                  type="password"
                  name="password"
                  id="password"
                  placeholder={t`Enter your new password`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-10 placeholder:text-placeholder-foreground"
                />
              </div>

              <Button
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
                disabled={newPassword?.length < 8}
                onClick={handlePasswordReset}
              >
                <Trans>Set password</Trans>
              </Button>
            </div>
          </>
        )}

        {uiState === "login" && (
          <>
            {/* Header */}
            <div className="text-center flex flex-col gap-2 mb-10">
              <h2 className="text-[30px] font-semibold leading-[36px] tracking-[-0.225px] text-foreground">
                <Trans>Password changed</Trans>
              </h2>
              <p className="text-base leading-7 text-muted-foreground">
                <Trans>Your password was successfully reset. You can now sign in using your new password</Trans>
              </p>
            </div>

            {/* Action */}
            <div className="flex flex-col gap-6 w-full">
              <Button
                asChild
                className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
              >
                <a href={`/login?${searchParams.toString()}`}><Trans>Login</Trans></a>
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
